import axios from "axios";
import { CommonTexts as Texts } from "../helpers/LayoutTexts";
import { getLanguageCode } from "../helpers/Common";
import { LoggedAdmin } from "../models/LoggedAdmin";

const baseUrl = Texts.apiBaseUrl + "video-lessons/";

const authHeaders = (admin: LoggedAdmin) => ({ 'Authorization': `Bearer ${admin.token}` });

export async function getVideoLessons(admin: LoggedAdmin, page: number, pageSize: number, status: string, teacherId?: number) {
    const response = await axios.get(baseUrl + "lessons-by-admin", {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, page, pageSize, status, teacherId, languageCode: getLanguageCode() }
    });
    return response.data;
}

export async function getVideoLessonsCount(admin: LoggedAdmin, teacherId?: number) {
    const response = await axios.get(baseUrl + "lessons-count-by-admin", {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, teacherId }
    });
    return response.data;
}

export async function getVideoLessonTeachers(admin: LoggedAdmin) {
    const response = await axios.get(baseUrl + "teachers-by-admin", {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId }
    });
    return response.data;
}

export async function getTeacherVideoSubjects(admin: LoggedAdmin, teacherId: number) {
    const response = await axios.get(baseUrl + "subjects-by-admin", {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, teacherId, languageCode: getLanguageCode() }
    });
    return response.data;
}

// Returns { subject, lesson }
export async function getVideoLesson(admin: LoggedAdmin, lessonId: number) {
    const response = await axios.get(baseUrl + "lesson-by-admin", {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, lessonId, languageCode: getLanguageCode() }
    });
    return response.data;
}

// formData carries the url-encoded lesson json as modelString and the optional lesson image as file
export async function saveVideoLesson(admin: LoggedAdmin, formData: FormData) {
    const response = await axios.post(baseUrl + "save-lesson-by-admin", formData, {
        headers: { ...authHeaders(admin), 'Content-Type': 'multipart/form-data' },
        params: { adminId: admin.adminId }
    });
    return response.data;
}

export async function approveVideoLesson(admin: LoggedAdmin, lessonId: number) {
    const response = await axios.post(baseUrl + "approve-lesson", null, {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, lessonId }
    });
    return response.data;
}

export async function deleteVideoLesson(admin: LoggedAdmin, lessonId: number) {
    const response = await axios.delete(baseUrl + "delete-lesson-by-admin", {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, lessonId }
    });
    return response.data;
}

// Videos and tutes go straight to S3 through a presigned url, then the API records the new file
export async function uploadLessonVideo(admin: LoggedAdmin, lessonId: number, video: File) {
    const urlResponse = await axios.get(baseUrl + "lesson-video-upload-url-by-admin", {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, lessonId, contentType: video.type }
    });
    const { uploadURL, timestamp } = urlResponse.data;
    await axios.put(uploadURL, video, { headers: { 'Content-Type': video.type } });
    await axios.post(baseUrl + "update-lesson-video-url-by-admin", null, {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, lessonId, timestamp }
    });
}

export async function uploadLessonTute(admin: LoggedAdmin, lessonId: number, tute: File) {
    const urlResponse = await axios.get(baseUrl + "lesson-tute-upload-url-by-admin", {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, lessonId, contentType: tute.type }
    });
    await axios.put(urlResponse.data, tute, { headers: { 'Content-Type': tute.type } });
    await axios.post(baseUrl + "update-lesson-tute-name-by-admin", null, {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, lessonId, tuteName: tute.name }
    });
}

// periodEndDate is a yyyy-mm-dd date; purchases on that date are included
export async function getPendingVideoPayouts(admin: LoggedAdmin, periodEndDate: string) {
    const response = await axios.get(baseUrl + "pending-payouts-by-admin", {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, periodEndDate }
    });
    return response.data;
}

export async function getPendingVideoPayoutPurchases(admin: LoggedAdmin, teacherId: number, periodEndDate: string) {
    const response = await axios.get(baseUrl + "pending-payout-purchases-by-admin", {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, teacherId, periodEndDate, languageCode: getLanguageCode() }
    });
    return response.data;
}

// formData carries the url-encoded pending payout json (with referenceNumber) as modelString and the optional slip as file
export async function transferVideoPayout(admin: LoggedAdmin, formData: FormData) {
    const response = await axios.post(baseUrl + "transfer-payout-by-admin", formData, {
        headers: { ...authHeaders(admin), 'Content-Type': 'multipart/form-data' },
        params: { adminId: admin.adminId }
    });
    return response.data;
}

export async function getVideoPayouts(admin: LoggedAdmin, page: number, pageSize: number, teacherId?: number) {
    const response = await axios.get(baseUrl + "payouts-by-admin", {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, page, pageSize, teacherId }
    });
    return response.data;
}

export async function getVideoPayoutPurchases(admin: LoggedAdmin, payoutId: number) {
    const response = await axios.get(baseUrl + "payout-purchases-by-admin", {
        headers: authHeaders(admin),
        params: { adminId: admin.adminId, payoutId, languageCode: getLanguageCode() }
    });
    return response.data;
}
